/**
 * WONE-612: shared method dispatcher for the unified email-first flow.
 *
 * Called from /auth/email (single-method auto-pick) and /auth/methods
 * (multi-method chooser). Returns a target path + optional SSO hint;
 * the caller decides whether to ``navigate(target)`` or
 * ``beginSSO(ssoProvider)``.
 *
 * Mobile parity: src/app/(auth)/email-entry/index.tsx ``navigateToMethod``.
 */

import { NavigateFunction } from "react-router-dom";

import { beginSSO, SsoProvider } from "providers/auth-tokens";

export type AuthUserState = "EXISTING" | "NEW" | "UNKNOWN_COMPANY";

export type { SsoProvider };

const SSO_PROVIDERS: ReadonlySet<SsoProvider> = new Set<SsoProvider>([
  "google-oauth2",
  "azuread-oauth2",
  "okta",
]);

export const isSsoProvider = (id: string): id is SsoProvider =>
  SSO_PROVIDERS.has(id as SsoProvider);

function encodeEmail(email: string): string {
  return encodeURIComponent(email);
}

/**
 * Dispatch the user to the appropriate next screen based on the
 * resolved auth backend id + user state.
 *
 * Branches:
 *   - ``email_otp``       → /{signin|signup}/email-otp/code?email=
 *   - ``email-and-pwd``   → /signin/email?email= (sign-in only;
 *                           Platform never offers password to NEW users)
 *   - SSO provider id     → window-redirect via beginSSO (no return)
 *   - anything else       → returns ``false`` so the caller can
 *                           surface a visible "method not supported"
 *                           inline error rather than silently no-op.
 *                           (Velislav review on #1172.)
 *
 * Returns ``true`` when a navigation dispatched, ``false`` on an
 * unknown id.
 *
 * ``returnTo`` is the upstream path the user was trying to reach before
 * sign-in; carried through SSO via the PKCE state so the user lands
 * where they expected after IdP round-trip.
 */
export async function navigateToMethod(args: {
  methodId: string;
  email: string;
  userState: AuthUserState;
  navigate: NavigateFunction;
  returnTo?: string;
}): Promise<boolean> {
  const { methodId, email, userState, navigate, returnTo } = args;
  const emailParam = encodeEmail(email);
  const isSignup = userState === "NEW";

  if (methodId === "email_otp") {
    navigate(
      `/${isSignup ? "signup" : "signin"}/email-otp/code?email=${emailParam}`,
      // Carry returnTo via router state — the OTP screen reads it after
      // a successful verify so deep-linked sign-ins land where the user
      // intended (parity with the SSO PKCE-state path).
      { state: returnTo ? { returnTo } : undefined }
    );
    return true;
  }

  if (methodId === "email-and-pwd") {
    navigate(`/signin/email?email=${emailParam}`, {
      state: returnTo ? { returnTo } : undefined,
    });
    return true;
  }

  if (isSsoProvider(methodId)) {
    await beginSSO(methodId, returnTo, email);
    return true;
  }

  // eslint-disable-next-line no-console
  console.warn(
    `[navigateToMethod] unrecognised auth backend id: ${methodId}`
  );
  return false;
}

/**
 * Client-side filter applied to ``methods`` before deciding auto-pick
 * vs chooser vs empty. Magic-link is hidden; password is offered only
 * when Platform includes it (``password_auth`` flag).
 */
export const HIDDEN_METHODS: ReadonlySet<string> = new Set<string>([
  "magic-link",
]);
